import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Tab,
    Tabs,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import RequestService from '../../services/RequestService';

const EditGroupDialog = ({ open, onClose, user, group, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        gameId: '',
        daysOfWeek: [],
        startTime: '',
        endTime: '',
    });

    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchGamesAndUsers = async () => {
            try {
                const gamesData = await RequestService.getGames(
                    user.company.id
                );
                const usersData = await RequestService.getUsersByCompany(
                    user.company.id
                );

                setGames(Array.isArray(gamesData) ? gamesData : []);
                setUsers(Array.isArray(usersData) ? usersData : []);
            } catch (err) {
                setError('Failed to load data.');
            }
        };

        fetchGamesAndUsers();
    }, [user.company.id]);

    useEffect(() => {
        if (group) {
            setFormData({
                name: group.name || '',
                description: group.description || '',
                gameId: group.gameId || '',
                daysOfWeek: group.daysOfWeek || [],
                startTime: group.startTime || '',
                endTime: group.endTime || '',
            });

            setSelectedUsers(group.users.map((u) => u.id) || []);
        }
    }, [group]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleGeneralInfoChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleUserSelection = (userId) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSaveGeneralInfo = async () => {
        try {
            const payload = {
                ...formData,
                companyId: group.companyId,
            };

            await RequestService.updateGroup(group.id, payload);
            onSuccess();
            onClose();
        } catch (err) {
            setError('Failed to update group information.');
        }
    };

    const handleSaveUsers = async () => {
        try {
            const initialUserIds = group.users.map((u) => u.id);
            const addedUsers = selectedUsers.filter(
                (id) => !initialUserIds.includes(id)
            );
            const removedUsers = initialUserIds.filter(
                (id) => !selectedUsers.includes(id)
            );

            if (addedUsers.length > 0) {
                const addPayload = { userIds: addedUsers };
                await RequestService.addUsersToGroup(group.id, addPayload);
            }

            if (removedUsers.length > 0) {
                const removePayload = { userIds: removedUsers };
                await RequestService.removeUsersFromGroup(
                    group.id,
                    removePayload
                );
            }

            onSuccess('Operações concluídas com sucesso!');
            onClose();
        } catch (err) {
            setError('Failed to update group users.');
        }
    };

    const handleToggleChange = (name) => (event, value) => {
        if (value !== null) {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Editar Grupo</DialogTitle>
            <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Informações do Grupo" />
                <Tab label="Gerenciar Usuários" />
            </Tabs>
            <DialogContent>
                {tabIndex === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Nome"
                            name="name"
                            value={formData.name}
                            onChange={handleGeneralInfoChange}
                            fullWidth
                        />
                        <TextField
                            label="Descrição"
                            name="description"
                            value={formData.description}
                            onChange={handleGeneralInfoChange}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="game-select-label">Jogo</InputLabel>
                            <Select
                                labelId="game-select-label"
                                name="gameId"
                                value={formData.gameId}
                                onChange={handleGeneralInfoChange}
                            >
                                {(games || []).map((game) => (
                                    <MenuItem key={game.id} value={game.id}>
                                        {game.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="body1">Dias da Semana</Typography>
                        <ToggleButtonGroup
                            value={formData.daysOfWeek}
                            onChange={handleToggleChange('daysOfWeek')}
                            fullWidth
                            multiple
                        >
                            <ToggleButton value={1}>Seg</ToggleButton>
                            <ToggleButton value={2}>Ter</ToggleButton>
                            <ToggleButton value={3}>Qua</ToggleButton>
                            <ToggleButton value={4}>Qui</ToggleButton>
                            <ToggleButton value={5}>Sex</ToggleButton>
                            <ToggleButton value={6}>Sáb</ToggleButton>
                            <ToggleButton value={7}>Dom</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            label="Hora de Início"
                            name="startTime"
                            type="time"
                            value={formData.startTime}
                            onChange={handleGeneralInfoChange}
                            fullWidth
                        />
                        <TextField
                            label="Hora de Fim"
                            name="endTime"
                            type="time"
                            value={formData.endTime}
                            onChange={handleGeneralInfoChange}
                            fullWidth
                        />
                    </Box>
                )}
                {tabIndex === 1 && (
                    <Box>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Selecionar</TableCell>
                                    <TableCell>Nome</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedUsers.includes(
                                                    user.id
                                                )}
                                                onChange={() =>
                                                    handleUserSelection(user.id)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{user.name}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                {tabIndex === 0 && (
                    <Button
                        onClick={handleSaveGeneralInfo}
                        color="primary"
                        variant="contained"
                    >
                        Salvar Alterações
                    </Button>
                )}
                {tabIndex === 1 && (
                    <Button
                        onClick={handleSaveUsers}
                        color="primary"
                        variant="contained"
                    >
                        Salvar Usuários
                    </Button>
                )}
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditGroupDialog;
